import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getToken, login } from "../api";

/**
 * v2 login - the first brand impression. A deep "night window"
 * backdrop (same gradient language as the Home hero) with a floating
 * card form on top. Pure CSS: no assets beyond the existing mark.
 */
export function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // Een iOS-beginschermapp start op de URL waar hij is toegevoegd -
  // vaak /login - en niet per se op de manifest-start_url. Met een
  // bewaarde sessie is dit scherm dan een dead end (16-09: elke start
  // opnieuw inloggen terwijl het token gewoon in localStorage stond).
  // Een verlopen token vangt authedFetch op (401 -> terug hierheen).
  if (getToken()) return <Navigate to="/" replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Inloggen mislukt");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col">
      {/* Brand panel */}
      <div className="hero-panel rounded-none border-x-0 border-t-0 px-6 pt-14 pb-20 text-center">
        <img
          src="/favicon.png"
          alt=""
          width={64}
          height={64}
          className="mx-auto select-none drop-shadow-lg"
          draggable={false}
        />
        <h1 className="mt-3 text-2xl font-bold tracking-tight" style={{ color: "var(--hero-ink)" }}>
          Mijn SlimStroom
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--hero-ink-mute)" }}>
          Inzicht in je thuisbatterij, zon en verbruik
        </p>
      </div>

      {/* Form card overlapping the panel */}
      <div className="flex-1 px-4 -mt-10 pb-10">
        <form
          onSubmit={onSubmit}
          className="card w-full max-w-sm mx-auto p-6 flex flex-col gap-4"
        >
          <h2 className="text-base font-semibold">Inloggen</h2>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-ink-mute">Gebruikersnaam</span>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="text-base bg-card-soft rounded-xl px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-accent border border-card-border"
              required
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-ink-mute">Wachtwoord</span>
            {/* text-base (16px) op de inputs: iOS Safari zoomt in op elk
                veld kleiner dan 16px en blijft in een beginschermapp
                ingezoomd staan (geen adresbalk om terug te tikken). */}
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="text-base bg-card-soft rounded-xl px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-accent border border-card-border"
              required
            />
          </label>
          {error && (
            <div
              className="text-sm text-danger bg-danger-soft rounded-xl p-3 border border-card-border"
              role="alert"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="pressable bg-accent text-white font-semibold rounded-xl py-3 shadow-glow disabled:opacity-50 disabled:shadow-none"
          >
            {busy ? "Bezig…" : "Inloggen"}
          </button>
        </form>
      </div>
    </div>
  );
}
